import {
  ACADEMY_LOADING,
  ACADEMY_SET_COURSES,
  ACADEMY_MARK_LESSON_COMPLETE,
  ACADEMY_ERROR
} from '../actions/academyActions';

/**
 * Initial state for the academy reducer.
 *
 * @type {Object}
 * @property {Array} courses - Array of courses with lessons.
 * @property {Object} progress - Overall progress stats.
 * @property {boolean} loading - Loading state.
 * @property {string|null} error - Error message if any.
 * @property {Array} progresses - Legacy: progress data for module-specific views.
 */
const initialState = {
  courses: [],
  progress: {
    completedLessons: 0,
    totalLessons: 0,
    percentage: 0
  },
  loading: false,
  error: null,
  // Legacy for module-specific views
  progresses: []
};

/**
 * Reducer function for managing academy-related state.
 *
 * @param {Object} state - The current state of the academy.
 * @param {Object} action - The dispatched action.
 * @returns {Object} The new state.
 */
const academyReducer = (state = initialState, action) => {
  // Ensure state has all required properties (fix for redux-persist restoring incomplete state)
  const currentState = {
    ...initialState,
    ...state
  };

  switch (action.type) {
    case ACADEMY_LOADING:
      return {
        ...currentState,
        loading: action.payload
      };

    case ACADEMY_SET_COURSES:
      return {
        ...currentState,
        courses: action.payload.courses || [],
        progress: action.payload.progress || initialState.progress,
        error: null
      };

    case ACADEMY_MARK_LESSON_COMPLETE: {
      const { videoId, courseId } = action.payload;

      // Update the lesson as completed in the courses array
      const updatedCourses = (currentState.courses || []).map((course) => {
        if (course.id === courseId) {
          return {
            ...course,
            lessons: (course.lessons || []).map((lesson) => (lesson.id === videoId
              ? { ...lesson, completed: true, completedAt: action.payload.completedAt }
              : lesson))
          };
        }
        return course;
      });

      // Recalculate progress
      const totalLessons = updatedCourses.reduce((acc, c) => acc + (c.lessons?.length || 0), 0);
      const completedLessons = updatedCourses.reduce(
        (acc, c) => acc + (c.lessons?.filter((l) => l.completed)?.length || 0),
        0
      );
      const percentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

      return {
        ...currentState,
        courses: updatedCourses,
        progress: {
          completedLessons,
          totalLessons,
          percentage
        }
      };
    }

    case ACADEMY_ERROR:
      return {
        ...currentState,
        error: action.payload
      };

    // Legacy actions for module-specific views
    case 'SET_ALL_PROGRESSES':
      return {
        ...currentState,
        progresses: action.payload.progresses || []
      };

    case 'UPDATE_OR_ADD_PROGRESS': {
      const { progress } = action.payload;
      return {
        ...currentState,
        progresses: (currentState.progresses || []).some((p) => p.id === progress.id)
          ? currentState.progresses.map((p) => (p.id === progress.id ? progress : p))
          : [...(currentState.progresses || []), progress]
      };
    }

    default:
      return currentState;
  }
};

export default academyReducer;
